import { useSeo } from "@/lib/seo";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, MessageCircle, Building2 } from "lucide-react";

export default function Chat() {
    useSeo({ title: "Messaggi | VendiDirect", description: "Le tue conversazioni con proprietari e agenzie.", noindex: true });
    const { user, loading } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (loading) return;
        if (!user) return;
        api.get("/chat/conversations").then((r) => {
            setConvs(r.data.items || []);
            // Auto-open if ?property=...&to=... params (start new convo)
            const to = params.get("to");
            const prop = params.get("property");
            if (to && prop) {
                const parts = [user.user_id, to].sort();
                const cid = `conv_${parts[0]}_${parts[1]}_${prop}`;
                setActive({ conversation_id: cid, other_id: to, property_id: prop, property_title: params.get("title") || "Annuncio" });
            }
        });
    }, [user, loading, params, navigate]);

    useEffect(() => {
        if (!active) return;
        const notifyRead = () => window.dispatchEvent(new Event("chat-read"));
        api.get(`/chat/${active.conversation_id}/messages`).then((r) => { setMessages(r.data.items || []); notifyRead(); });
        const it = setInterval(() => {
            api.get(`/chat/${active.conversation_id}/messages`).then((r) => { setMessages(r.data.items || []); notifyRead(); }).catch(() => {});
        }, 5000);
        return () => clearInterval(it);
    }, [active]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e.preventDefault();
        if (!input.trim() || sending || !active) return;
        setSending(true);
        try {
            await api.post("/chat/messages", { property_id: active.property_id, to_user_id: active.other_id, message: input });
            setInput("");
            const r = await api.get(`/chat/${active.conversation_id}/messages`);
            setMessages(r.data.items || []);
        } catch (err) {
        } finally {
            setSending(false);
        }
    };

    const [guestName, setGuestName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [guestMessage, setGuestMessage] = useState("");
    const [guestSent, setGuestSent] = useState(false);
    const [guestError, setGuestError] = useState(null);

    const sendGuestMessage = async (e) => {
        e.preventDefault();
        if (!guestName.trim() || !guestEmail.trim() || !guestMessage.trim()) {
            setGuestError("Compila nome, email e messaggio.");
            return;
        }
        setGuestError(null);
        setSending(true);
        try {
            await api.post("/chat/messages", {
                property_id: params.get("property"),
                to_user_id: params.get("to"),
                message: guestMessage,
                guest_name: guestName,
                guest_email: guestEmail,
            });
            setGuestSent(true);
        } catch (err) {
            setGuestError(err.response?.data?.detail || "Errore nell'invio del messaggio");
        } finally {
            setSending(false);
        }
    };

    if (!user) {
        const toId = params.get("to");
        const propId = params.get("property");
        const title = params.get("title") || "l'annuncio";
        if (!toId || !propId) {
            return (
                <div data-testid="guest-chat-empty" className="pt-32 max-w-xl mx-auto px-6 text-center text-ink-400 text-sm">
                    Apri un annuncio e clicca "Chatta col proprietario" per inviare un messaggio.
                </div>
            );
        }
        if (guestSent) {
            return (
                <div data-testid="guest-chat-sent" className="pt-32 max-w-xl mx-auto px-6 text-center">
                    <MessageCircle size={32} className="text-gold mx-auto mb-4" />
                    <h1 className="font-serif text-2xl text-ink-50 mb-2">Messaggio inviato</h1>
                    <p className="text-ink-400 text-sm">Riceverai una risposta all'indirizzo email che hai indicato.</p>
                </div>
            );
        }
        return (
            <div data-testid="guest-chat-form-page" className="pt-24 sm:pt-28 pb-12 max-w-xl mx-auto px-6">
                <span className="label-eyebrow">Messaggi</span>
                <h1 className="font-serif text-2xl sm:text-3xl text-ink-50 mt-2 mb-2">Scrivi per {decodeURIComponent(title)}</h1>
                <p className="text-ink-400 text-sm mb-8">Lascia nome ed email: riceverai la risposta senza bisogno di registrarti.</p>
                <form onSubmit={sendGuestMessage} data-testid="guest-chat-form" className="space-y-4">
                    <input data-testid="guest-chat-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Nome e cognome" className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none text-sm" />
                    <input data-testid="guest-chat-email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="Email" className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none text-sm" />
                    <textarea data-testid="guest-chat-message" rows={4} value={guestMessage} onChange={(e) => setGuestMessage(e.target.value)} placeholder="Scrivi il tuo messaggio..." className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none text-sm" />
                    {guestError && <div data-testid="guest-chat-error" className="text-red-400 text-xs">{guestError}</div>}
                    <button data-testid="guest-chat-submit" disabled={sending} type="submit" className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                        <Send size={14} /> {sending ? "Invio..." : "Invia messaggio"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="chat-page" className="pt-24 sm:pt-28 pb-4 sm:pb-12 w-full px-3 sm:px-8 lg:px-16 2xl:px-24">
            <span className="label-eyebrow hidden sm:inline">Messaggi</span>
            <h1 className="font-serif text-2xl sm:text-4xl text-ink-50 mt-2 sm:mt-3 mb-4 sm:mb-8">Chat con i proprietari</h1>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 border border-white/10" style={{ minHeight: "60vh" }}>
                {/* Conversations */}
                <aside className={`lg:col-span-4 border-b lg:border-b-0 lg:border-r border-white/5 overflow-y-auto overscroll-contain ${active ? "hidden lg:block" : "block"}`} style={{ maxHeight: "70vh" }}>
                    {convs.length === 0 ? (
                        <div className="p-8 text-center text-ink-400 text-sm">
                            <MessageCircle size={24} className="mx-auto mb-3 text-gold" />
                            Nessuna conversazione. Apri un annuncio e clicca "Chatta col proprietario".
                        </div>
                    ) : (
                        convs.map((c) => (
                            <button
                                key={c.conversation_id}
                                data-testid={`conv-${c.conversation_id}`}
                                onClick={() => setActive(c)}
                                className={`w-full text-left p-5 border-b border-white/5 hover:bg-white/5 transition-colors ${active?.conversation_id === c.conversation_id ? "bg-white/5 border-l-2 border-l-gold" : ""}`}
                            >
                                <div className="flex items-center gap-2 text-xs text-gold mb-1"><Building2 size={11} /> {c.property_title}</div>
                                <div className="text-sm text-ink-100 truncate">{c.last_message}</div>
                                <div className="text-xs text-ink-400 mt-1 flex justify-between">
                                    <span>{new Date(c.last_at).toLocaleDateString("it-IT")}</span>
                                    {c.unread > 0 && <span className="bg-gold text-black px-2 rounded-full text-[10px] font-bold">{c.unread}</span>}
                                </div>
                            </button>
                        ))
                    )}
                </aside>

                {/* Thread */}
                <main className={`lg:col-span-8 flex-col ${active ? "flex" : "hidden lg:flex"}`} style={{ maxHeight: "70vh" }}>
                    {active ? (
                        <>
                            <div className="p-3 sm:p-5 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                    <button
                                        type="button"
                                        onClick={() => setActive(null)}
                                        aria-label="Torna alle conversazioni"
                                        className="lg:hidden text-ink-400 hover:text-gold px-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                                    >‹</button>
                                    <Building2 size={14} className="text-gold shrink-0" />
                                    <span className="font-serif text-base sm:text-lg text-ink-50 truncate" data-testid="chat-thread-title">{active.property_title}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-gold/30 text-gold bg-gold/5">
                                        Chat dedicata a questo annuncio
                                    </span>
                                    {active.property_id && (
                                        <a
                                            href={`/annunci/${active.property_id}`}
                                            data-testid="chat-open-property"
                                            className="text-[11px] text-ink-400 hover:text-gold underline"
                                        >
                                            Vedi annuncio
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3" style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch" }}>
                                {messages.map((m) => {
                                    const mine = m.from_user_id === user.user_id;
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div
                                                data-testid={mine ? "chat-bubble-out" : "chat-bubble-in"}
                                                className={`max-w-[80%] sm:max-w-[75%] px-4 py-3 text-sm rounded-2xl break-words whitespace-pre-wrap shadow-[0_4px_10px_rgba(0,0,0,0.28),0_1px_2px_rgba(0,0,0,0.18)] ${mine ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900 border border-gray-200"}`}
                                            >
                                                <div className="leading-relaxed">{m.message}</div>
                                                <div className={`text-[10px] mt-1.5 ${mine ? "text-white/70" : "text-gray-500"}`}>
                                                    {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {messages.length === 0 && <div className="text-center text-ink-400 text-sm pt-12">Inizia la conversazione 👋</div>}
                            </div>
                            <form onSubmit={send} className="p-3 sm:p-4 border-t border-white/10 flex gap-2 items-center" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
                                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi un messaggio..." className="flex-1 bg-transparent border border-white/15 px-4 py-3 text-base sm:text-sm text-ink-50 focus:border-gold outline-none min-h-[44px]" />
                                <button data-testid="chat-send" disabled={sending} className="bg-gold text-black px-5 hover:bg-gold-soft disabled:opacity-50 min-h-[44px] min-w-[52px] flex items-center justify-center"><Send size={16} /></button>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-ink-400 text-sm">Seleziona una conversazione</div>
                    )}
                </main>
            </div>
        </div>
    );
}
